import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Field, KlantSelect, KANS_STATUS, eur, fmtDate, klantNaamFrom, HelpBar } from "./salesShared";
import { Plus, Trash2, Pencil } from "lucide-react";

const empty = { klantCode: "", titel: "", waarde: "", status: "Lead", verwachteDatum: "", notitie: "", redenVerloren: "" };

export default function PijplijnTab({ klanten, kansen, reload }) {
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const klantNaam = (c) => klantNaamFrom(klanten, c);

  const save = async () => {
    if (!form.titel.trim()) return toast.error("Titel is verplicht.");
    const body = { ...form, klantNaam: klantNaam(form.klantCode) };
    try {
      if (editId) await api.put(`/sales/kansen/${editId}`, body);
      else await api.post("/sales/kansen", body);
      toast.success("Kans opgeslagen"); setForm(empty); setEditId(null); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const edit = (k) => { setEditId(k.id); setForm({ ...empty, ...k }); };
  const del = async (k) => { if (!window.confirm("Kans verwijderen?")) return; try { await api.delete(`/sales/kansen/${k.id}`); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const setStatus = async (k, status) => {
    let redenVerloren = k.redenVerloren || "";
    if (status === "Verloren" && !redenVerloren) {
      redenVerloren = window.prompt("Reden verloren? (bijv. prijs, levertijd, concurrent)") || "";
    }
    try { await api.put(`/sales/kansen/${k.id}`, { ...k, status, redenVerloren }); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-slate-800">Verkooppijplijn</h2>
      <HelpBar>Een <b>kans</b> schuift door vier fases. <b>Lead</b> = eerste interesse/contact, nog geen aanbod. <b>Offerte</b> = prijsvoorstel uitgebracht, wacht op antwoord. <b>Gewonnen</b> = deal binnen (telt mee in "Gewonnen waarde"). <b>Verloren</b> = gaat niet door (noteer de reden zodat we ervan leren). Vul per kans in: klant, korte titel en de verwachte waarde in euro's.</HelpBar>
      <div className="rounded-lg border border-slate-200 bg-white p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Klant"><KlantSelect klanten={klanten} value={form.klantCode} onChange={(v) => setForm({ ...form, klantCode: v })} testid="kans-klant" /></Field>
        <Field label="Titel / kans"><Input data-testid="kans-titel" value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} placeholder="Bijv. Jaarcontract kipproducten" /></Field>
        <Field label="Waarde (€)"><Input type="number" step="0.01" data-testid="kans-waarde" value={form.waarde} onChange={(e) => setForm({ ...form, waarde: e.target.value })} /></Field>
        <Field label="Status"><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="kans-status"><SelectValue /></SelectTrigger><SelectContent>{KANS_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Verwachte datum"><Input type="date" data-testid="kans-datum" value={form.verwachteDatum} onChange={(e) => setForm({ ...form, verwachteDatum: e.target.value })} /></Field>
        {form.status === "Verloren" && <Field label="Reden verloren" hint="Waarom ging de kans niet door?"><Input data-testid="kans-reden" value={form.redenVerloren} onChange={(e) => setForm({ ...form, redenVerloren: e.target.value })} placeholder="bijv. prijs, levertijd, concurrent" /></Field>}
        <div className="flex items-end gap-2">
          <Button data-testid="kans-save" onClick={save} className="bg-[#1C64B0] hover:bg-[#154E8A]"><Plus className="h-4 w-4 mr-1" /> {editId ? "Bijwerken" : "Kans toevoegen"}</Button>
          {editId && <Button variant="outline" onClick={() => { setEditId(null); setForm(empty); }}>Annuleren</Button>}
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        {KANS_STATUS.map((s) => {
          const items = kansen.filter((k) => k.status === s);
          const w = items.reduce((x, k) => x + (Number(k.waarde) || 0), 0);
          return (
            <div key={s} data-testid="kans-col" className="rounded-lg border border-slate-200 bg-slate-50 p-2">
              <div className="flex items-center justify-between px-1 pb-2">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{s}</span>
                <span className="text-[11px] text-slate-400">{items.length} · {eur(w)}</span>
              </div>
              <div className="space-y-2">
                {items.map((k) => (
                  <div key={k.id} data-testid="kans-card" className="rounded-md border border-slate-200 bg-white p-2">
                    <div className="font-semibold text-sm text-slate-800">{k.titel}</div>
                    <div className="text-[11px] text-slate-500">{k.klantNaam || k.klantCode} · {eur(k.waarde)}</div>
                    {k.verwachteDatum && <div className="text-[11px] text-slate-400">verwacht {fmtDate(k.verwachteDatum)}</div>}
                    {k.status === "Verloren" && k.redenVerloren && <div className="text-[11px] text-rose-500 mt-0.5">Reden: {k.redenVerloren}</div>}
                    <div className="flex items-center gap-1 mt-1">
                      <Select value={k.status} onValueChange={(v) => setStatus(k, v)}><SelectTrigger data-testid="kans-card-status" className="h-7 text-xs flex-1"><SelectValue /></SelectTrigger><SelectContent>{KANS_STATUS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid="kans-edit" onClick={() => edit(k)}><Pencil className="h-3.5 w-3.5 text-slate-500" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid="kans-delete" onClick={() => del(k)}><Trash2 className="h-3.5 w-3.5 text-slate-400" /></Button>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <p className="text-[11px] text-slate-400 px-1">Leeg</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
